import React, { useEffect, useRef, useState } from "react";
import { useLeadMeta } from "./leadUtils";

// The lead's details, as on the website form — used to add a lead by hand and
// to edit one. `campaigns` given = adding (pick the campaign).
const EMPTY = { fullName: "", mobile: "", altMobile: "", email: "", education: "", educationCourse: "", address: "", city: "", state: "", campaignId: "" };

// Outside LeadForm on purpose: a component defined inside it would be a new
// type on every render, remounting the inputs and losing focus on each key.
const Field = ({ error, label, required, children, hint }) => (
  <label className={`ld-field${error ? " is-invalid" : ""}`}>
    <span>{label}{required && <em>*</em>}</span>
    {children}
    {hint && <span className="ld-field-hint">{hint}</span>}
    {error && <span className="ld-field-error">{error}</span>}
  </label>
);

const LeadForm = ({ initial, campaigns, onSubmit, onCancel, submitLabel = "Save" }) => {
  const meta = useLeadMeta();
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const formRef = useRef(null);
  // Editing a field clears its error: the highlight then only marks what is still wrong.
  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((errs) => {
      if (!errs[k]) return errs;
      const { [k]: _gone, ...rest } = errs;
      return rest;
    });
  };

  // After a rejected save, bring the first highlighted field into view.
  useEffect(() => {
    const first = formRef.current?.querySelector(".ld-field.is-invalid input, .ld-field.is-invalid select");
    if (first) { first.scrollIntoView({ block: "center", behavior: "smooth" }); first.focus({ preventScroll: true }); }
  }, [errors]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    setMessage("");
    try {
      await onSubmit(form);
    } catch (err) {
      setErrors(err.errors || {});
      setMessage(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Errors for something this form has no field for are spelled out in the banner,
  // so "Please correct the highlighted fields" never points at nothing.
  const shown = new Set([...(campaigns ? ["campaignId"] : []), ...Object.keys(EMPTY).filter((k) => k !== "campaignId")]);
  const unshown = Object.entries(errors).filter(([k]) => !shown.has(k)).map(([, v]) => v);

  return (
    <form onSubmit={submit} ref={formRef} noValidate>
      {message && (
        <div className="ld-alert ld-alert-error">
          {message}
          {unshown.length > 0 && <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>{unshown.map((m) => <li key={m}>{m}</li>)}</ul>}
        </div>
      )}
      {campaigns && (
        <Field error={errors.campaignId} label="Campaign" required hint="The campaign decides the course and its automated messages.">
          <select className="ld-select" value={form.campaignId} onChange={set("campaignId")}>
            <option value="">Select a campaign…</option>
            {campaigns.map((c) => <option key={c._id} value={c._id}>{c.name} — {c.courseTitle}</option>)}
          </select>
        </Field>
      )}
      <div className="ld-grid-2">
        <Field error={errors.fullName} label="Full name" required><input className="ld-input" value={form.fullName} onChange={set("fullName")} /></Field>
        <Field error={errors.email} label="Email address" required><input className="ld-input" type="email" value={form.email} onChange={set("email")} /></Field>
        <Field error={errors.mobile} label="Mobile number" required><input className="ld-input" inputMode="numeric" maxLength={14} value={form.mobile} onChange={set("mobile")} /></Field>
        <Field error={errors.altMobile} label="Alternative number"><input className="ld-input" inputMode="numeric" maxLength={14} value={form.altMobile} onChange={set("altMobile")} /></Field>
        <Field error={errors.education} label="Last / current education" required>
          <select className="ld-select" value={form.education} onChange={set("education")}>
            <option value="">Select…</option>
            {(meta?.educationLevels || []).map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </Field>
        <Field error={errors.educationCourse} label="Last / current education course" required><input className="ld-input" placeholder="e.g. B.Sc Computer Science" value={form.educationCourse} onChange={set("educationCourse")} /></Field>
      </div>
      <Field error={errors.address} label="Address"><input className="ld-input" value={form.address} onChange={set("address")} /></Field>
      <div className="ld-grid-2">
        <Field error={errors.city} label="City" required><input className="ld-input" value={form.city} onChange={set("city")} /></Field>
        <Field error={errors.state} label="State" required><input className="ld-input" value={form.state} onChange={set("state")} /></Field>
      </div>
      <div className="ld-modal-foot" style={{ padding: "14px 0 0", borderTop: "none" }}>
        {onCancel && <button type="button" className="ld-btn" onClick={onCancel}>Cancel</button>}
        <button type="submit" className="ld-btn ld-btn-primary" disabled={busy}>{busy ? "Saving…" : submitLabel}</button>
      </div>
    </form>
  );
};

export default LeadForm;
